import {
  CarCarePageContent,
  getLinks,
  getStoryBySlug,
  PageProps,
  StoryblokGetLinksResponse,
  StoryData,
  useStoryblokState,
} from '@cinch-nx/data-storyblok'
import { VapsFeatureCarCare } from '@in-life-and-loyalty/vaps-feature-car-care'
import { Head } from '@in-life-and-loyalty/vaps-ui'
import type { GetStaticPaths, GetStaticProps } from 'next'

import { servicingStoryFallback } from '../data/servicing-story-fallback'

function CarCarePage({
  story,
  preview,
  generatedAt,
}: PageProps<CarCarePageContent>) {
  const { content, slug } = useStoryblokState<CarCarePageContent>({
    story,
    enableBridge: preview,
  })
  const SEO = content?.SEO?.[0]
  return (
    <>
      <Head
        {...SEO}
        generatedAt={generatedAt}
        fallbackTitle={slug}
        fallbackDescription={
          'Learn about our paint, glass and interior protection product, CarCare.'
        }
      />
      {content && <VapsFeatureCarCare content={content} />}
    </>
  )
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
}) => {
  const slug = params?.slug as string
  const now = Date.now()

  const buildStaticPropsResult = (story: StoryData<CarCarePageContent>) => {
    const heroProps = story.content?.hero[0]

    return {
      props: {
        story,
        preview,
        generatedAt: now,
        layoutProps: {
          heroProps,
          showBorderRadius: true,
          showFooterBorderRadius: false,
        },
      },
      revalidate: 60, // In seconds
    }
  }

  try {
    const story = await getStoryBySlug<CarCarePageContent>(`car-care/${slug}`, {
      cv: now,
      version: preview ? 'draft' : 'published',
    })

    return buildStaticPropsResult(story)
  } catch {
    if (slug === 'servicing') {
      return buildStaticPropsResult(servicingStoryFallback)
    }

    return { notFound: true } as const
  }
}

type Path = { params: { slug: string } }

export function carCareStaticPaths({
  links,
}: {
  links: StoryblokGetLinksResponse['links']
}) {
  const paths: Path[] = []

  for (const linkKey of Object.keys(links)) {
    if (links[linkKey].is_folder) {
      continue
    }

    const slug = links[linkKey].slug.split('/')[1]

    paths.push({ params: { slug } })
  }

  return paths
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Get the paths we want to pre-render based on data
  try {
    const links = await getLinks('car-care')
    const paths = carCareStaticPaths({ links })

    return { paths, fallback: 'blocking' }
  } catch {
    return { paths: [], fallback: 'blocking' }
  }
}

export default CarCarePage
